import { gql } from "@apollo/client";
import { FEES_FOR_PAX, BREAKDOWN, SSRS } from "./fragments";

export const CREATE_TRIP = gql`
  mutation booking($request: Input_BookingSellRequest) {
    bookingReset

    tripCreatev4(request: $request) {
      bookingKey
      recordLocator
      breakdown {
        balanceDue
        totalAmount
        authorizedBalanceDue
        journeys {
          key
          value {
            totalAmount
            totalDiscount
          }
        }
        journeyTotals {
          totalAmount
          totalDiscount
        }
        passengers {
          key
          value {
            convenience {
              charges {
                amount
                currencyCode
                foreignAmount
                foreignCurrencyCode
              }
              taxes
              adjustments
              total
            }
            infant {
              charges {
                amount
                currencyCode
                foreignAmount
                foreignCurrencyCode
              }
              taxes
              adjustments
              total
            }
            upgrades {
              charges {
                amount
                currencyCode
                foreignAmount
                foreignCurrencyCode
              }
              taxes
              adjustments
              total
            }
          }
        }
      }
      journeys {
        flightType
        stops
        designator {
          arrival
          departure
          destination
          origin
        }
        segments {
          segmentKey
          cabinOfService
          designator {
            arrival
            departure
            destination
            origin
          }
          passengerSegment {
            key
            value {
              passengerKey
              liftStatus
              ssrs {
                count
                feeCode
                inBundle
                isConfirmed
                note
                ssrCode
                ssrDetail
                ssrKey
                ssrNumber
              }
            }
          }
          flightReference
          identifier {
            carrierCode
            identifier
            opSuffix
          }
          international
        }
      }
    }
  }
`;

export const COMMIT = gql`
  mutation addPaymentAndCommitBooking(
    $bookingCommitRequest: Input_CommitRequestv2
  ) {
    bookingCommit: bookingCommitv3(request: $bookingCommitRequest, allowConcurrentChanges: false) {
      selfServiceMoveAvailable
    }
  }
`;

export const SEAT_SELL = gql`
  ${FEES_FOR_PAX}
  ${BREAKDOWN}
  mutation sellSeats($request: input_seat_data) {
    seatAdd: seatAddv2(request: $request)
  }
  query results {
    booking {
      seats: journeys {
        ...seats
      }
      breakdown {
        ...breakdown
      }
      fees: passengers {
        ...fees
      }
    }
  }
  fragment seats on Journey {
    segments {
      segmentKey
      passengerSegment {
        passengerKey: key
        value {
          seats {
            arrivalStation
            compartmentDesignator
            departureStation
            passengerKey
            penalty
            seatInformation {
              propertyList {
                key
                value
              }
            }
            unitDesignator
            unitKey
          }
        }
      }
    }
  }

  fragment charge on ChargeBreakdown {
    charges {
      amount
      collectType
      code
      currencyCode
      detail
      type
    }
    taxes
    total
  }
`;

export const SELL_SSR = gql`
  mutation sell($request: Input_SsrByKeysRequestv2) {
    ssrsAddv2(request: $request) {
      count
      feeCode
      passengerKey
      ssrCode
      ssrKey
      ssrNumber
      market {
        departureDate
        destination
        origin
      }
    }
  }
`;

export const RESELL_SSR = gql`
  mutation($request: Input_ResellSsrRequest) { resellSsrs(request: $request){
      count,
      feeCode,
      passengerKey,
      ssrCode 
    } 
  }
`;

export const CREATE_TRIP_ONLY = gql`
  mutation booking($request: Input_TripSellRequest) {
    # Reset the booking before creating a new one
    bookingReset
    tripAddv4(request: $request) {
      bookingKey
      recordLocator
      passengers {
        key
        value {
          name {
            first
            last
          }
          passengerTypeCode
          infant {
            name {
              first
              last
            }
          }
        }
      }
      breakdown {
        balanceDue
        totalAmount
        authorizedBalanceDue
        journeys {
          key
          value {
            totalAmount
            totalDiscount
            totalPoints
            totalTax
          }
        }
        journeyTotals {
          totalAmount
          totalDiscount
          totalPoints
          totalTax
        }
        passengers {
          key
          value {
            passengerKey
            convenience {
              charges {
                amount
                currencyCode
                foreignAmount
                foreignCurrencyCode
              }
              taxes
              adjustments
              total
            }
            infant {
              charges {
                amount
                currencyCode
                foreignAmount
                foreignCurrencyCode
              }
              taxes
              adjustments
              total
            }
            upgrades {
              charges {
                amount
                currencyCode
                foreignAmount
                foreignCurrencyCode
              }
              taxes
              adjustments
              total
            }
            seats {
              total
              taxes
              charges {
                amount
                code
                currencyCode
                detail
                type
              }
              adjustments
            }
          }
        }
      }
      journeys {
        flightType
        stops
        designator {
          arrival
          departure
          destination
          origin
        }
        segments {
          legs {
            designator {
              arrival
              departure
              destination
              origin
            }
            flightReference
            legInfo {
              arrivalTerminal
              arrivalTime
              arrivalTimeUtc
              departureTerminal
              departureTimeUtc
              departureTime
            }
            ssrs {
              ssrNestCode
              lid
              available
              sold
              unitSold
            }
          }
          cabinOfService
          designator {
            arrival
            departure
            destination
            origin
          }
          passengerSegment {
            key
            value {
              passengerKey
              liftStatus
              modifiedDate
              ssrs {
                ssrCode
              }
            }
          }
          flightReference
          identifier {
            carrierCode
            identifier
            opSuffix
          }
          international
        }
      }
    }
  }
`;

export const UPDATE_PASSENGER_DETAILS = gql`
  mutation updatePassengerDetail($contact: Input_Contact) {
    contactsAdd(request: $contact) {
      contactTypeCode
    }
  }
`;

export const UPDATE_PASSENGER_DOB = gql`
  mutation updatePassengerDetail($passengers: [Input_PassengerUpdateRequestv2]) {
    passengersSetBatch(requests: $passengers) {
      passengerKey
    }
  }
`;

export const BOOKING_CANCEL_ALL1 = gql`
  mutation cancelAllJourneys {
    journeyDeleteAll
  }
`;

export const BOOKING_CANCEL_ALL2 = gql`
  ${FEES_FOR_PAX}
  ${BREAKDOWN}
  ${SSRS}
  query response {
    booking {
      info {
        status
      }
      recordLocator
      journeys {
        flightType
        designator {
          ...designator
        }
        journeyKey
        segments {
          designator {
            ...designator
          }
          segmentKey
          identifier {
            identifier
            carrierCode
            opSuffix
          }
          cabinOfService
          international
          isStandby
          legs {
            designator {
              ...designator
            }
            legKey
            legInfo {
              arrivalTimeUtc
              departureTimeUtc
            }
          }
        }
        stops
      }
      ssrs: journeys {
        ...ssrs
      }
      breakdown {
        ...breakdown
      }
      passengerFaresReference: journeys {
        journeyKey
        segments {
          fares {
            passengerFares {
              passengerType
              serviceCharges {
                amount
                collectType
                code
                currencyCode
                detail
                type
              }
            }
          }
          segmentKey
          flightReference
          legs {
            legKey
            flightReference
          }
        }
      }
      fees: passengers {
        ...fees
      }
      payments {
        paymentKey
        amounts {
          amount
          collected
        }
        details {
          accountNumber
          installments
          parentPaymentId
          accountName
          expirationDate
          fields {
            key
            value
          }
        }
        voucher {
          overrideAmount
        }
        status
        authorizationStatus
        code
        createdDate
      }
    }
  }
  fragment designator on TransportationDesignator {
    origin
    destination
    arrival
    departure
  }

  fragment charge on ChargeBreakdown {
    charges {
      amount
      collectType
      code
      currencyCode
      detail
      type
    }
    taxes
    total
  }
`;

export const BOOKING_COMMIT1 = gql`
  mutation commitBooking($bookingCommitRequest: Input_CommitRequestv2) {
    bookingCommit: bookingCommitv3(request: $bookingCommitRequest) {
      recordLocator
    }
  }
`;

export const UPDATE_PASSENGER_DETAILS_WITH_GST = gql`
  mutation updatePassengerDetail($contact: Input_Contact, $gst: Input_Contact) {
    contactsAdd(request: $contact) {
      contactTypeCode
    }
    gst: contactsAdd(request: $gst) {
      contactTypeCode
    }
  }
`;

export const UPDATE_PRIMARY_CONTACT_DETAILS = gql`
  mutation ($request: Input_ContactRequest, $contactTypeCode: String) {
    contactsSet(contactTypeCode: $contactTypeCode, request: $request) {
      name {
        first
        middle
        last
        title
        suffix
      }
      emailAddress
      phoneNumbers {
        number
        type
      }
      customerNumber
      cultureCode
      notificationPreference
      distributionOption
    }
  }
`;

export const PASSENGER_CONTACT_INFANT_UPDATE = gql`
  mutation ($request: Input_ContactRequest, $contactTypeCode: String) {
    contactsSet(contactTypeCode: $contactTypeCode, request: $request) {
      name {
        first
        middle
        last
        title
        suffix
      }
      emailAddress
      phoneNumbers {
        number
        type
      }
      customerNumber
      cultureCode
      notificationPreference
      distributionOption
    }
  }
`;

export const ADD_PASSENGER_CONTACT = gql`
  mutation ($request: Input_Contact) {
    contactsAdd(request: $request) {
      name {
        first
        middle
        last
        title
        suffix
      }
      emailAddress
      phoneNumbers {
        number
        type
      }
      customerNumber
      cultureCode
      notificationPreference
      distributionOption
    }
  }
`;

export const SELL_FEE_CODE = gql`
  mutation {
    feeAdd(request: { feeCode: "INSU" }) {
      code
      type
      passengerFeeKey
    }
  }
`;

export const BOOKING_RESET = gql`
  mutation {
    bookingReset
  }
`;

export function createSSRSellUnSellMutation(ssrToSell:String[], ssrToUnsell:String[], ssrNote:String[]) {
  // Create the unsell mutation by mapping over ssrToUnsell and creating dynamic aliases
  const unSellMutation = ssrToUnsell
    .map((k, i) => `ssrsDelete${i}: ssrsDelete(ssrKey: "${k}")`)
    .join("\n");

  // Create the sell mutation by mapping over ssrToSell and adding notes dynamically
  const sellMutation = `ssrsAddv2(request: { 
    keys: [
      ${ssrToSell
        .map((k) => {
          return `{ ssrKey: "${k}" }`;
        })
        .join(",")}
    ]
  }) { 
    count
    ssrKey
  }`;

  const finalMutation = `
    mutation sellSSR {
      ${ssrToSell.length > 0 ? sellMutation : ""}
      ${ssrToUnsell.length > 0 ? unSellMutation : ""}
    }
  `;

  // Combine both mutations conditionally
  return gql`${finalMutation}`;
}
